import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../lib/AuthContext";
import { API } from "../lib/api";
import { LogOut } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthButton({ compact = false, naked = false }) {
    const { user, loading, logout, refresh } = useAuth();
    const [providers, setProviders] = useState({ google: true, apple: false });

    useEffect(() => {
        axios.get(`${API}/auth/providers`).then(({ data }) => setProviders(data)).catch(() => {});
    }, []);

    if (loading) return null;

    const handleGoogle = () => {
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const handleApple = async () => {
        if (!window.AppleID) {
            // Load Apple SDK on demand.
            await new Promise((res, rej) => {
                const s = document.createElement("script");
                s.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
                s.onload = res;
                s.onerror = rej;
                document.head.appendChild(s);
            }).catch(() => {});
        }
        if (!window.AppleID) {
            console.warn("Apple SDK failed to load");
            return;
        }
        // Init from /auth/providers payload (clientId comes server-side ideally, but Apple JS needs it client-side).
        // We expose APPLE_CLIENT_ID via a tiny GET below — fallback to empty silent-fail.
        try {
            const { data: cfg } = await axios.get(`${API}/auth/apple/config`);
            window.AppleID.auth.init({
                clientId: cfg.client_id,
                scope: "name email",
                redirectURI: window.location.origin + "/",
                usePopup: true,
            });
            const res = await window.AppleID.auth.signIn();
            const id_token = res?.authorization?.id_token;
            const nameObj = res?.user?.name;
            const fullName = nameObj ? `${nameObj.firstName || ""} ${nameObj.lastName || ""}`.trim() : null;
            if (!id_token) throw new Error("No identity_token from Apple");
            await axios.post(`${API}/auth/apple/session`, { identity_token: id_token, name: fullName }, { withCredentials: true });
            await refresh();
        } catch (e) {
            console.warn("Apple sign-in failed", e);
        }
    };

    if (user) {
        return (
            <div className="flex items-center gap-2" data-testid="auth-user">
                {user.picture ? (
                    <img src={user.picture} alt="" referrerPolicy="no-referrer"
                        className={naked ? "h-7 w-7 rounded-full object-cover" : "h-7 w-7 rounded-full border border-app/40 object-cover"} />
                ) : (
                    <div className={
                        naked
                            ? "h-7 w-7 rounded-full bg-surface-2 flex items-center justify-center font-sans text-[10px] text-app"
                            : "h-7 w-7 rounded-full bg-surface-2 border border-app/40 flex items-center justify-center font-sans text-[10px] text-app"
                    }>
                        {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                    </div>
                )}
                {!compact && (
                    <span className="font-sans text-xs text-app-2 max-w-[110px] truncate">{user.name || user.email}</span>
                )}
                <button onClick={logout} aria-label="Sign out" data-testid="auth-logout"
                    className={
                        naked
                            ? "flex items-center justify-center h-9 w-9 text-app-3 hover:text-app transition-colors"
                            : "p-1.5 rounded-full text-app-3 hover:text-app hover:bg-surface-2 transition-colors"
                    }>
                    <LogOut className={naked ? "w-5 h-5" : "w-3.5 h-3.5"} strokeWidth={1.5} />
                </button>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2" data-testid="auth-buttons">
            <button
                onClick={handleGoogle}
                data-testid="auth-login-button"
                title={compact ? "Sign in with Google" : "Continue with Google"}
                aria-label={compact ? "Sign in with Google" : "Continue with Google"}
                className={
                    naked && compact
                        ? "flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                        : compact
                        ? "flex items-center justify-center h-9 w-9 rounded-full border border-app/30 bg-surface-1 text-app hover:bg-surface-2 transition-colors"
                        : "flex items-center gap-2 px-3 py-1.5 rounded-full border border-app/40 bg-surface-2 text-app hover:bg-surface-3 transition-colors"
                }
            >
                <svg width={(naked && compact) ? "18" : compact ? "16" : "12"} height={(naked && compact) ? "18" : compact ? "16" : "12"} viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M21.35 11.1h-9.17v2.92h5.5c-.26 1.37-1.04 2.53-2.21 3.31v2.74h3.57c2.08-1.92 3.29-4.74 3.29-8.04 0-.66-.06-1.3-.17-1.93Z"/>
                    <path fill="currentColor" d="M12.18 22c2.97 0 5.46-.98 7.28-2.66l-3.57-2.74c-.99.66-2.25 1.05-3.71 1.05-2.86 0-5.28-1.93-6.14-4.52H2.36v2.84A10 10 0 0 0 12.18 22Z"/>
                    <path fill="currentColor" d="M6.04 13.13a5.97 5.97 0 0 1 0-3.83V6.46H2.36a10 10 0 0 0 0 8.51l3.68-1.84Z"/>
                    <path fill="currentColor" d="M12.18 5.94c1.62 0 3.07.56 4.21 1.65l3.16-3.16C17.62 2.71 15.14 1.72 12.18 1.72a10 10 0 0 0-9.82 6.18l3.68 1.84c.86-2.59 3.28-4.52 6.14-4.52Z"/>
                </svg>
                {!compact && (
                    <span className="font-sans text-[11px] uppercase tracking-[0.25em]">Continue with Google</span>
                )}
            </button>
            {providers.apple && (
                <button
                    onClick={handleApple}
                    data-testid="auth-apple-button"
                    title={compact ? "Sign in with Apple" : "Continue with Apple"}
                    aria-label={compact ? "Sign in with Apple" : "Continue with Apple"}
                    className={
                        naked && compact
                            ? "flex items-center justify-center h-9 w-9 text-app-2 hover:text-app transition-colors"
                            : compact
                            ? "flex items-center justify-center h-9 w-9 rounded-full border border-app/30 bg-surface-1 text-app hover:bg-surface-2 transition-colors"
                            : "flex items-center gap-2 px-3 py-1.5 rounded-full border border-app/40 bg-surface-2 text-app hover:bg-surface-3 transition-colors"
                    }
                >
                    <svg width={(naked && compact) ? "18" : compact ? "16" : "12"} height={(naked && compact) ? "18" : compact ? "16" : "12"} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                        <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.493-.12-1.13.42-2.29 1.117-3.061.789-.871 2.013-1.503 3.047-1.512zM21 17.295c-.586 1.36-.87 1.97-1.62 3.17-1.05 1.67-2.53 3.74-4.37 3.76-1.63.01-2.05-1.06-4.27-1.04-2.22.02-2.67 1.06-4.3 1.05-1.84-.02-3.24-1.9-4.29-3.56-2.93-4.65-3.24-10.1-1.43-13 .82-1.33 2.39-2.45 4.04-2.49 1.6-.04 3.1 1.08 4.16 1.08 1.04 0 2.82-1.34 4.74-1.14.81.03 3.05.33 4.49 2.45-.12.07-2.68 1.57-2.65 4.69.03 3.73 3.26 4.97 3.3 4.99z"/>
                    </svg>
                    {!compact && (
                        <span className="font-sans text-[11px] uppercase tracking-[0.25em]">Continue with Apple</span>
                    )}
                </button>
            )}
        </div>
    );
}
